import { GLOSSARY_DATA, type GlossaryEntry } from "~/data/glossaryData";

export interface TextToken {
  type: "text";
  content: string;
}

export interface TermToken {
  type: "term";
  term: string;
  originalText: string;
  entry: GlossaryEntry;
}

export type Token = TextToken | TermToken;

interface SearchableTerm {
  phrase: string;
  entry: GlossaryEntry;
  regex: RegExp;
}

// Pre-compute searchable terms sorted by length descending (longest-match first)
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getSearchableTerms(): SearchableTerm[] {
  const list: { phrase: string; entry: GlossaryEntry }[] = [];

  for (const entry of Object.values(GLOSSARY_DATA)) {
    list.push({ phrase: entry.term, entry });
    if (entry.aliases) {
      for (const alias of entry.aliases) {
        list.push({ phrase: alias, entry });
      }
    }
  }

  // Sort descending by phrase length (longest first)
  list.sort((a, b) => b.phrase.length - a.phrase.length);

  return list.map(({ phrase, entry }) => {
    // Word boundary check: ensure we don't match inside a word (e.g., "oil" in "boiled")
    // Support non-ascii accented chars like é in Papier-mâché / Chine collé
    const escaped = escapeRegex(phrase);
    const regex = new RegExp(`(?<=^|[^\\p{L}\\p{N}])${escaped}(?=[^\\p{L}\\p{N}]|$)`, "giu");
    return {
      phrase,
      entry,
      regex,
    };
  });
}

interface MatchInterval {
  start: number;
  end: number;
  originalText: string;
  entry: GlossaryEntry;
}

/**
 * Tokenizes a free-text medium description into plain text and clickable glossary terms.
 * Resolves compound sentences with multiple terms independently, longest-match first.
 */
export function tokenizeMediumText(input: string | null | undefined): Token[] {
  if (!input) return [];

  const searchTerms = getSearchableTerms();
  const matches: MatchInterval[] = [];

  // Find all matches for each term
  for (const item of searchTerms) {
    const regex = new RegExp(item.regex.source, item.regex.flags);
    let match: RegExpExecArray | null;

    while ((match = regex.exec(input)) !== null) {
      const start = match.index;
      const end = start + match[0].length;
      const originalText = match[0];

      // Check if this overlaps with an already found longer match
      const overlaps = matches.some(
        (existing) =>
          (start >= existing.start && start < existing.end) ||
          (end > existing.start && end <= existing.end) ||
          (start <= existing.start && end >= existing.end),
      );

      if (!overlaps) {
        matches.push({
          start,
          end,
          originalText,
          entry: item.entry,
        });
      }
    }
  }

  // Sort all non-overlapping matches by start position
  matches.sort((a, b) => a.start - b.start);

  // Build token stream
  const tokens: Token[] = [];
  let currentIndex = 0;

  for (const match of matches) {
    if (match.start > currentIndex) {
      tokens.push({
        type: "text",
        content: input.slice(currentIndex, match.start),
      });
    }

    tokens.push({
      type: "term",
      term: match.entry.term,
      originalText: match.originalText,
      entry: match.entry,
    });

    currentIndex = match.end;
  }

  if (currentIndex < input.length) {
    tokens.push({
      type: "text",
      content: input.slice(currentIndex),
    });
  }

  return tokens;
}

/**
 * Secondary fallback service for uncurated terms.
 * Queries Wikipedia REST API and flags the entry as unreviewed.
 */
export async function fetchWikipediaGlossaryFallback(term: string): Promise<GlossaryEntry | null> {
  try {
    const cleanTerm = encodeURIComponent(term.trim());
    const res = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${cleanTerm}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data && data.extract) {
      // Flag internally that this term needs manual art-curation
      console.warn(`[Glossary] Unreviewed Wikipedia fallback triggered for term: "${term}"`, {
        title: data.title,
        description: data.description,
      });

      return {
        term: data.title || term,
        category: "Digital & mixed media",
        subFamily: "digital-mixed-combine",
        definition: data.extract,
      };
    }
  } catch (err) {
    console.error(`[Glossary] Wikipedia fallback failed for "${term}":`, err);
  }
  return null;
}
